import { GoldBtn, SectionLabel, ImageLightbox } from '@/components/ui';
import { C } from '@/theme/tokens';
import { useState } from 'react';
import { useIsMobile } from '@/hooks/isMobile';

const highlights = [
  { icon: '✦', text: 'New Quests — added continuously' },
  { icon: '✦', text: "GM Toolkit beta — unlocks after we're funded" },
  { icon: '✦', text: 'Homebrew system — early access for backers' },
];

const discordbullets = [
  { icon: '✦', text: 'Get early previews of upcoming Quests and features' },
  { icon: '✦', text: 'Vote on what we build next' },
  { icon: '✦', text: 'Report bugs and shape the game directly' },
];

/** Reusable clickable image thumbnail that opens the lightbox */
function ClickableImage({
  src,
  alt,
  onOpen,
  style,
}: {
  src: string;
  alt: string;
  onOpen: (src: string) => void;
  style?: React.CSSProperties;
}) {
  return (
    <img
      src={src}
      alt={alt}
      onClick={() => onOpen(src)}
      style={{
        objectFit: 'contain',
        borderRadius: 8,
        cursor: 'zoom-in',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.02)';
        e.currentTarget.style.boxShadow = '0 0 24px rgba(212,175,55,0.25)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    />
  );
}

/**
 * "We Ship Continuously" — a compact confidence-building strip between
 * How It Works and the Reserve CTA. Communicates that QuestXR is a living
 * product that improves throughout (and beyond) the campaign, and invites
 * backers to help shape it.
 *           QuestXR is already playable (<a href="https://qxrgame.com" target="_blank">see for yourself</a>) — and it keeps getting better. We're building in the open: new
          features, new Quests, and new tools drop throughout the pre-campaign, campaign, and beyond.
          Founding Backers get the best stuff first.
 */
export function ContinuousDeliverySection() {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const isMobile = useIsMobile();

  return (
    
    <section
      id="continuous-delivery"
      style={{
        background: `radial-gradient(ellipse at 50% 30%, #1a3550 0%, #0a1a2a 70%)`,
        padding: '36px 40px',
        display: 'grid',
        gridTemplateColumns: '26% 48% 27%',
        gap: 12,
        alignItems: 'start',
        boxSizing: 'border-box',
        maxWidth: '100vw',
        overflow: 'hidden',

      }}
    >
        <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          alignSelf: 'center',
        }}
      >
        <div style={{ display: isMobile ? 'block' : 'none', textAlign: 'center' }}>
        <SectionLabel>Built in the Open</SectionLabel>

        <h2
          style={{
            fontFamily: "'Cinzel',serif",
            fontSize: 'clamp(1.4rem,4vw,2rem)',
            fontWeight: 900,
            letterSpacing: '2px',
            color: '#fff',
            textTransform: 'uppercase',
            marginBottom: 8,
            lineHeight: 1.2,
          }}
        >
          This Isn't Vaporware.{' '}
          <br />
          <span style={{ color: C.gold }}>We Ship Continuously.</span>
        </h2>
        </div>
        <ClickableImage
          src="/assets/phone with hand.png"
          alt="App Preview — tap to view"
          onOpen={setLightboxSrc}
          style={{ width: isMobile ? '50%' : '100%' }}
        />
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
        <SectionLabel style={{ display: isMobile ? 'none' : 'block' }}>Built in the Open</SectionLabel>

        <h2
          style={{
            fontFamily: "'Cinzel',serif",
            fontSize: 'clamp(1.8rem,5vw,2.8rem)',
            fontWeight: 900,
            letterSpacing: '2px',
            color: '#fff',
            textTransform: 'uppercase',
            marginBottom: 8,
            lineHeight: 1.2,
            display: isMobile ? 'none' : 'block',
          }}
        >
          We Ship{' '}
          <br />
          <span style={{ color: C.gold }}>Continuously.</span>
        </h2>

        <div>
        <p
          style={{
            fontSize: 15,
            color: 'rgba(255,255,255,0.65)',
            lineHeight: 1.75,
            maxWidth: 560,
            margin: '0 auto 18px',
          }}
        >
          QuestXR is already playable. We're just putting on the finishing touches before play testing launches on July 1st, and it will keep getting better. We'll be building in the open: new
          features, new Quests, and new tools drop throughout the pre-campaign, campaign, and beyond.
        </p>
        <p
          style={{
            fontSize: 18,
            color: C.gold,
            lineHeight: 1.75,
            maxWidth: 560,
            margin: '0 auto 18px',
          }}
        >
          Founding Backers get the best stuff first.
        </p>
        </div>

        {/* Highlight bullets */}
        <div
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            gap: 14,
            textAlign: 'left',
            marginBottom: 40,
          }}
        >
          {highlights.map((h) => (
            <div
              key={h.text}
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'center',
              }}
            >
              <span style={{ color: C.gold, fontSize: 12, flexShrink: 0 }}>{h.icon}</span>
              <span
                style={{
                  fontSize: 14,
                  color: 'rgba(255,255,255,0.75)',
                  fontWeight: 400,
                  letterSpacing: '0.3px',
                }}
              >
                {h.text}
              </span>
            </div>
          ))}
        </div>

        {/* Build With Us callout */}
        <div
          style={{
            padding: '28px 32px',
            background: 'rgba(20,38,59,0.6)',
            border: '2px solid #b8922a',
            borderLeft: '6px solid #c9a84c',
            borderRadius: 5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Top accent line */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: 'linear-gradient(to right, transparent, #C19A6B 30%, #D4AF37 50%, #C19A6B 70%, transparent)',
            }}
          />

          <SectionLabel>Build With Us</SectionLabel>
                  <h2
          style={{
            fontFamily: "'Cinzel',serif",
            fontSize: 'clamp(1rem,2.6vw,1.4rem)',
            fontWeight: 900,
            letterSpacing: '2px',
            color: '#fff',
            textTransform: 'uppercase',
            marginBottom: 8,
            lineHeight: 1.2,
          }}
        >
          Join The Adventure
        </h2>
            {/* Discord bullets */}
            <div
            style={{
                display: 'inline-flex',
                flexDirection: 'column',
                gap: 14,
                textAlign: 'left',
                marginBottom: 40,
            }}
            >
            {discordbullets.map((db) => (
                <div
                key={db.text}
                style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    marginBottom: '-12px',
                }}
                >
                <span style={{ color: C.gold, fontSize: 12, flexShrink: 0 }}>{db.icon}</span>
                <span
                    style={{
                    fontSize: 14,
                    color: 'rgba(255,255,255,0.75)',
                    fontWeight: 400,
                    letterSpacing: '0.3px',
                    }}
                >
                    {db.text}
                </span>
                </div>
            ))}
            </div>
            <br />
          <GoldBtn variant="outlined" href="https://discord.gg/Upjm9K3b6" style={{ fontSize: 16, padding: '9px 24px', color: '#c9a84c', borderColor: '#c9a84c' }}>
            Join the Discord →
          </GoldBtn>
        </div>
      </div>
      <div
        className="cd-screenshot-grid"
        style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 6,
        alignItems: 'start',
        alignContent: 'start',
        overflow: 'hidden',
        boxSizing: 'border-box',
        maxWidth: '100%',
        paddingTop: isMobile ? '24px' : '0px',
      }}>
        <div>
          <ClickableImage
            src="/assets/vw-chars.png"
            alt="Characters View — tap to view"
            onOpen={setLightboxSrc}
            style={{ width: '66%', border: '1px solid #cfa020' }}
          />
        </div>
        <div>
          <ClickableImage
            src="/assets/vw-tutorial.png"
            alt="Tutorial View — tap to view"
            onOpen={setLightboxSrc}
            style={{ width: '66%', border: '1px solid #cfa020' }}
          />
        </div>
        <div>
          <ClickableImage
            src="/assets/vw-quest-card.png"
            alt="Quest Card — tap to view"
            onOpen={setLightboxSrc}
            style={{ width: '66%', border: '1px solid #cfa020' }}
          />
        </div>
        <div>
          <ClickableImage
            src="/assets/vw-keypad.png"
            alt="Community View — tap to view"
            onOpen={setLightboxSrc}
            style={{ width: '66%', border: '1px solid #cfa020' }}
          />
        </div>
        <div>
          <ClickableImage
            src="/assets/vw-quests.png"
            alt="Quests View — tap to view"
            onOpen={setLightboxSrc}
            style={{ width: '66%', border: '1px solid #cfa020' }}
          />
        </div>
        <div>
          <ClickableImage
            src="/assets/vw-combat.png"
            alt="Combat View — tap to view"
            onOpen={setLightboxSrc}
            style={{ width: '66%', border: '1px solid #cfa020' }}
          />
        </div>
      </div>

      {/* Fullscreen lightbox — shows whichever image was clicked */}
      {lightboxSrc && (
        <ImageLightbox
          src={lightboxSrc}
          alt="Full Size View"
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </section>
  );
}
